import { RangeSlider } from "../range-slider.model";
import { CountValueFacetItem, Facet } from "./facet.model";

export interface SliderBounds {
  min: number;
  max: number;
  includeInvalidInFilter?: boolean;
}

export class SliderFacet extends Facet {
  unit: string;
  step: number;
  slider: RangeSlider;
  bounds?: SliderBounds;

  constructor(
    name: string,
    unit: string,
    step: number,
    facets: CountValueFacetItem[],
    bounds?: SliderBounds
  ) {
    super(name, facets);

    this.unit = unit;
    this.step = step;
    this.bounds = bounds;
    this.slider = new RangeSlider(unit, step, +this.minValue, +this.maxValue);
  }

  get selected(): boolean {
    return this.slider.sliderSelected;
  }

  filter(urlParams: URLSearchParams): string {
    const param = urlParams.get(this.name);
    if (!param) {
      this.slider = new RangeSlider(this.unit, this.step, +this.minValue, +this.maxValue);
      return null;
    }

    const [floor, min, max, ceil] = param.split(",");
    const floorVal = +floor;
    const minVal = +min;
    const maxVal = +max;
    const ceilVal = +ceil;

    const { bounds } = this;
    const minBound = bounds?.min ?? 0;
    const maxBound = bounds?.max ?? 0;

    if (bounds && (floorVal < minBound || ceilVal > maxBound || minVal < minBound || maxVal > maxBound)) {
      this.slider = new RangeSlider(this.unit, this.step, +this.minValue, +this.maxValue);
      return null;
    }

    this.slider.minValue = minVal;
    this.slider.maxValue = maxVal;
    this.slider.floor = floorVal;
    this.slider.ceil = ceilVal;

    if (this.slider.ceil === this.slider.maxValue && this.slider.floor === this.slider.minValue) {
      return null;
    }

    const rangeFilter = `(${this.name} gt ${this.slider.minValue} or ${this.name} eq ${this.slider.minValue})
           and (${this.name} lt ${this.slider.maxValue} or ${this.name} eq ${this.slider.maxValue})`;

    if (bounds?.includeInvalidInFilter) {
      if (this.slider.minValue === minBound && this.slider.maxValue === maxBound) {
        return null;
      }
      const invalidFilter = `(${this.name} eq null or ${this.name} lt ${minBound} or ${this.name} gt ${maxBound})`;
      return `(${rangeFilter} or ${invalidFilter})`;
    }

    return rangeFilter;
  }

  update = (facets: CountValueFacetItem[]) => {
    // if this facet is selected do not update
    if (this.slider.sliderSelected) {
      return;
    }

    this.facets = facets;
    const round = (val: number) => (this.bounds ? val : this.roundDownToNearestThousand(val));
    const minVal = round(+this.minValue);
    const maxVal = round(+this.maxValue);
    this.slider = new RangeSlider(this.unit, this.step, minVal, maxVal);
  };

  // since we are using a range we need to reset the min and max value
  // this is technically unchecking a facet
  uncheck = () => {
    this.slider.minValue = this.slider.floor;
    this.slider.maxValue = this.slider.ceil;
  };

  roundDownToNearestThousand(num: number): number {
    return num < 1000 ? num : Math.floor(num / 1000) * 1000;
  }

  toQueryParam = (): string => {
    if (!this.selected) {
      return undefined;
    }

    return `${this.slider.floor},${this.slider.minValue},${this.slider.maxValue},${this.slider.ceil}`;
  };
}
